import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormButton } from '../../Button';
import { getReq, postReqfileupload } from '../../../apis/api';
import { useSnackbar } from 'notistack';
import { FaSpinner } from 'react-icons/fa6';

// "YYYY-MM-DDTHH:MM" now on the browser's (Indian) clock - the latest a stage can be logged at
const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
// a stored stage time ("2026-10-18 09:15:00" or "2026-10-18T09:15") as the date-time box takes it
const toLocalInput = (v) => {
  const m = String(v || "").match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : "";
};

const EditStagesModal = ({ open, handleClose, stageData, subitemID = "", split_id = "", onStageUpdated = () => { } }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [stageNames, setStageNames] = useState([]);
  const [quantityError, setQuantityError] = useState('');

  const [formData, setFormData] = useState({
    stage_id: '',
    emp_id: '',
    date_time: '',
    quantity: '',
    status: 'pending'
  });

  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    const fetchData = async () => {
      try {
        const empResponse = await getReq('fetchEmployee');
        if (empResponse.status === 200) {
          setEmployees(empResponse.data || []);
        }

        const stageResponse = await getReq(`subitemStages/${subitemID}`);
        if (stageResponse.status === 200) {
          const stageNamesData = stageResponse.data?.stage_names || [];
          setStageNames(stageNamesData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Error fetching data', { variant: 'error' });
      }
    };

    if (open && subitemID) {
      fetchData();
    }
  }, [open, subitemID, enqueueSnackbar]);

  useEffect(() => {
    if (stageData) {
      setFormData({
        stage_id: stageData.stage_id || '',
        emp_id: stageData.emp_id || stageData.employee_id || '',
        // SGEW 19 Oct (field audit): a time saved by the server ("... 09:15:00") showed blank in the box, and the save then
        // stopped at "Please fill all required fields"
        date_time: toLocalInput(stageData.date_time || stageData.date),
        quantity: stageData.quantity || '',
        status: stageData.status || 'pending'
      });
    }
  }, [stageData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.stage_id || !formData.emp_id || !formData.date_time || !formData.quantity) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }

    if (!split_id || !stageData?.split_subitemid || !stageData?.id) {
      enqueueSnackbar('Missing identifiers for update', { variant: 'error' });
      return;
    }

    try {
      setIsLoading(true);


      const updateData = new FormData();
      updateData.append('stage_id', formData.stage_id);
      updateData.append('emp_id', formData.emp_id);
      updateData.append('date_time', formData.date_time);
      updateData.append('quantity', formData.quantity);
      updateData.append('weight', stageData.weight);


      const response = await postReqfileupload(
        updateData,
        `/updateSplitStages/${split_id}/${stageData.split_subitemid}/${stageData.id}`
      );

      if (response.status === 200) {
        enqueueSnackbar(response?.data?.message || 'Stage updated successfully', { variant: 'success' });
        onStageUpdated();
        handleClose();
      }
    } catch (error) {
      console.error('Error updating stage:', error);

      const backendMessage =
        error.response?.data?.message || 'Error updating stage';

      if (backendMessage.toLowerCase().includes('quantity')) {
        setQuantityError(backendMessage);
      } else {
        enqueueSnackbar(backendMessage, { variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      stage_id: '',
      emp_id: '',
      date_time: '',
      quantity: '',
      status: 'pending'
    });
    handleClose();
  };



  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialogContent-root": {
          padding: "24px !important",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h6" fontWeight={600}>
          Edit Stage
        </Typography>
        <IconButton aria-label="close" onClick={handleCloseModal} sx={{ color: "#6b7280" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Stage</InputLabel>
              <Select
                value={formData.stage_id}
                label="Stage"
                onChange={(e) => handleInputChange('stage_id', e.target.value)}
              >
                {stageNames.length > 0 ? (
                  stageNames.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No stages available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.emp_id}
                label="Employee"
                onChange={(e) => handleInputChange('emp_id', e.target.value)}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.user_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date & Time"
              type="datetime-local"
              value={formData.date_time}
              onChange={(e) => handleInputChange('date_time', e.target.value)}
              size="small"
              inputProps={{ max: nowLocal() }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                handleInputChange('quantity', isNaN(value) ? '' : value);
                setQuantityError('');
              }}
              size="small"
              inputProps={{ min: 1 }}
              error={
                !formData.quantity || formData.quantity <= 0 || !!quantityError
              }
              helperText={
                !formData.quantity || formData.quantity <= 0
                  ? 'Quantity must be greater than 0'
                  : quantityError || ''
              }

            />
          </Grid>


          {/* SGEW 19 Oct (field audit): the entry's status is the approval's (pending until the Admin approves the
              sub-item's stages) - the Status box here was sent and never saved */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Status"
              value={formData.status === "approved" ? "Approved" : "Waiting for approval"}
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Weight"
              value={stageData?.weight || ''}
              size="small"
              disabled
              helperText="Weight (read-only)"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <FormButton
          type="submit"
          title="Update Stage"
          func={handleSubmit}
          isLoading={isLoading}
          spinner={<FaSpinner />}
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditStagesModal;